import {Box, Button, MenuItem, Select, SelectChangeEvent} from '@mui/material'
import {api2Endpoint} from 'app/config/apiConfig'
import {RawDataFileRepo} from 'app/hooks/RawDataFileRepo'
import {getData} from 'app/hooks/api/useGetData'
import {usePolling} from 'app/hooks/usePolling'
import {CUserFile} from 'app/modules/discovery/core'
import {DateTime} from 'luxon'
import {useRef, useState} from 'react'
import styled from 'styled-components'
import {JsonObjectWithId} from 'utilities/types'
import AdminWrapper from './AdminWrapper'

const PrettyFileInput = styled.input`
  flex: 0 0 auto;
  visibility: hidden;
  width: 120px;
  padding: 1px;
  ::before {
    content: 'Select File...';
    visibility: visible;
    border: 1px solid #ddd;
    padding: 6px 12px;
    border-radius: 4px;
    background-color: #eee;
    cursor: pointer;
  }
  :hover::before {
    background-color: white;
  }
`

const PrettyFileInputLabel = styled.div`
  flex: 1 1 auto;
  padding: 3px 12px;
  border: 1px solid #ddd;
`
type JobStatus = {
  id: string
  name: string
  status: string
  error_message: string | null
  startTime: DateTime
  endTime: DateTime | null
}

export default function GraphManagementPage() {
  const tables = {
    'Innovation Data Files': 'innovation_from_upload',
    'University Data Files': 'university_from_upload',
    'Opportunity Data Files': 'opportunity_from_upload',
  }
  type TableLabelType = keyof typeof tables
  const tableLabels = Object.keys(tables) as TableLabelType[]
  const [fileObj, setFileObj] = useState<File | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [tableLabel, setTableLabel] = useState<TableLabelType>('Innovation Data Files')
  const tableName = tables[tableLabel]

  const createRawFile = RawDataFileRepo.useCreate()
  const listRawFiles = RawDataFileRepo.useList({tableName})
  const deleteRawFile = RawDataFileRepo.useDelete()
  const [progressUploading, setProgressUploading] = useState(false)
  const [autobuildJob, setAutobuildJob] = useState<JobStatus | null>(null)
  const [currentTime, setCurrentTime] = useState<DateTime>(DateTime.now())

  // if the most recent job has no end time, then we want to keep a running total of its
  // duration which updates 1/sec.
  function updateCurrentTime() {
    setCurrentTime(DateTime.now())
  }
  usePolling(updateCurrentTime, 1_000)

  async function pollForUpdates() {
    const jobs = await getData<Record<string, JsonObjectWithId>>(api2Endpoint('jobs'))
    if (jobs['autobuild'] == undefined) {
      setAutobuildJob(null)
    } else {
      const job = jobs['autobuild']
      if (
        typeof job.start_timestamp !== 'string' ||
        (typeof job.end_timestamp !== 'string' && job.end_timestamp !== null) ||
        typeof job.status !== 'string' ||
        (typeof job.error_message !== 'string' && job.error_message !== null)
      ) {
        throw new Error(`Invalid job data: ${JSON.stringify(job)}`)
      }

      const startTime = DateTime.fromISO(job.start_timestamp, {zone: 'utc'}).toLocal()
      const endTime = job.end_timestamp
        ? DateTime.fromISO(job.end_timestamp, {zone: 'utc'}).toLocal()
        : null
      setAutobuildJob({
        id: job.id,
        name: 'autobuild',
        status: job.status,
        error_message: job.error_message,
        startTime,
        endTime,
      })
    }
  }
  usePolling(pollForUpdates, 10_000)

  const autobuildDuration = autobuildJob
    ? (autobuildJob.endTime ?? currentTime).diff(autobuildJob.startTime)
    : null

  async function uploadBtn() {
    if (!fileObj) {
      throw new Error(`You must select a file to upload first.`)
    }
    try {
      setProgressUploading(true)
      await createRawFile.mutateAsync({file: fileObj, tableName})
      setProgressUploading(false)
    } catch (err) {
      setProgressUploading(false)
      throw err
    }
  }

  async function downloadBtn(file: CUserFile) {
    await file.download()
  }

  async function deleteBtn(file: CUserFile) {
    if (file.id !== undefined) {
      await deleteRawFile.mutateAsync({tableName, fileId: file.id})
    }
  }

  function statusColor(status: string) {
    if (status === 'complete') {
      return '#80ff60'
    }
    if (status === 'running') {
      return '#d8d840'
    }
    if (status === 'error') {
      return '#ffa0a0'
    }
    return '#e0e0e0'
  }

  return (
    <AdminWrapper>
      <Box sx={{backgroundColor: '#eeeeee', padding: '24px', minHeight: 'calc(100vh - 72px)'}}>
        <Box>
          <Box sx={{textAlign: 'center'}}>
            <Select
              sx={{fontWeight: 'bold', fontSize: '18px', margin: '12px', backgroundColor: 'white'}}
              value={tableLabel}
              onChange={(event: SelectChangeEvent) => {
                setTableLabel(event.target.value as TableLabelType)
              }}
            >
              {tableLabels.map((label) => (
                <MenuItem key={label} value={label}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {listRawFiles.data?.map((rawFile) => {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid #ccc',
                  backgroundColor: 'white',
                  padding: '0 12px',
                }}
                key={rawFile.id}
              >
                <Box sx={{flex: '1 1 auto'}}>{rawFile.name}</Box>
                <Box sx={{flex: '0 0 auto', width: '150px'}}>
                  {rawFile.created?.at
                    ? DateTime.fromISO(rawFile.created?.at).toLocaleString(DateTime.DATETIME_SHORT)
                    : ''}
                </Box>
                <Button onClick={() => downloadBtn(rawFile)}>Download</Button>
                <Button onClick={() => deleteBtn(rawFile)}>Delete</Button>
              </Box>
            )
          })}
        </Box>
        <Box sx={{display: 'flex'}}>
          <Box sx={{flex: '1 1 auto'}}>&nbsp;</Box>
          <Box
            sx={{
              marginTop: '12px',
              display: 'flex',
              alignItems: 'center',
              flex: '0 0 auto',
              padding: '12px',
              backgroundColor: 'white',
            }}
          >
            <Box sx={{flex: '0 0 auto', display: 'flex', minWidth: '300px', marginRight: '12px'}}>
              <PrettyFileInput
                ref={fileRef}
                type='file'
                onChange={() =>
                  setFileObj(fileRef?.current?.files?.length ? fileRef.current.files[0] : null)
                }
              ></PrettyFileInput>
              <PrettyFileInputLabel style={fileObj ? {} : {fontStyle: 'italic', opacity: 0.5}}>
                {fileObj ? fileObj.name : 'no file selected'}
              </PrettyFileInputLabel>
            </Box>
            <Box sx={{flex: '1 1 auto'}}>
              <Button
                sx={{fontSize: '14px'}}
                onClick={uploadBtn}
                disabled={progressUploading || !fileObj}
              >
                Upload
              </Button>
            </Box>
          </Box>
          <Box sx={{flex: '1 1 auto'}}>&nbsp;</Box>
        </Box>
        {autobuildJob ? (
          <Box>
            <Box
              sx={{
                marginTop: '36px',
                display: 'flex',
                backgroundColor: 'white',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  flex: '0 0 auto',
                  fontWeight: 'bold',
                  margin: '10px 20px',
                  justifyContent: 'center',
                  fontSize: '16px',
                }}
              >
                <Box>Latest Graph Build</Box>
              </Box>
              <Box
                sx={{
                  flex: '0 0 auto',
                  margin: '10px 20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{fontWeight: 'bold'}}>Status</Box>
                <Box
                  sx={{
                    backgroundColor: statusColor(autobuildJob.status),
                    padding: '3px 10px',
                    width: '75px',
                    textAlign: 'center',
                  }}
                >
                  {autobuildJob.status}
                </Box>
              </Box>
              <Box
                sx={{
                  flex: '0 0 auto',
                  margin: '10px 20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{fontWeight: 'bold'}}>Start Time</Box>
                <Box sx={{padding: '3px 0', width: '128px', textAlign: 'center'}}>
                  {autobuildJob.startTime?.toLocaleString(DateTime.DATETIME_SHORT)}
                </Box>
              </Box>
              <Box
                sx={{
                  flex: '0 0 auto',
                  margin: '10px 20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{fontWeight: 'bold'}}>End Time</Box>
                <Box sx={{padding: '3px 0', width: '128px', textAlign: 'center'}}>
                  {autobuildJob.endTime?.toLocaleString(DateTime.DATETIME_SHORT) ?? '\u00A0'}
                </Box>
              </Box>
              <Box
                sx={{
                  flex: '0 0 auto',
                  margin: '10px 20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{fontWeight: 'bold'}}>Duration</Box>
                <Box sx={{padding: '3px 0', width: '80px', textAlign: 'center'}}>
                  {autobuildDuration?.toFormat('h:mm:ss') ?? (autobuildJob ? '0:00:00' : '')}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: '0 0 auto',
                padding: '12px',
                fontWeight: 'bold',
                backgroundColor:
                  autobuildJob?.status === 'error' ? statusColor('error') : '#808080',
              }}
            >
              {autobuildJob.error_message}
            </Box>
          </Box>
        ) : (
          <></>
        )}
      </Box>
    </AdminWrapper>
  )
}
